import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material'
import NoticeModal from '_component/modules/notice/NoticeModal'
import dayjs from 'dayjs'
import {useEffect, useRef, useState} from 'react'
import {
  DEFAULT_NOTICE_CONFIG,
  NOTICE_CONFIG_PATH,
  NoticeConfig,
  NoticeHighlight,
  clearNoticeDismissal,
  getNoticeConfigStatus,
  loadNoticeConfig,
  parseNoticeConfig,
  stringifyNoticeConfig,
} from 'utils/config/noticeConfig'
import {normalAlert, resultAlert} from 'utils/CustomAlert/Alert'

/** nginx .htpasswd 에 등록한 계정명. 비밀번호는 코드에 두지 않는다. */
const CONFIG_AUTH_USER = 'admin'

const EMPTY_HIGHLIGHT: NoticeHighlight = {label: '', date: '', time: '', note: ''}

/** datetime-local 입력이 다루는 형식. 초·타임존을 붙이면 값이 안 들어간다. */
const toInputValue = (iso: string | null) => (iso ? dayjs(iso).format('YYYY-MM-DDTHH:mm') : '')
const fromInputValue = (value: string) => (value === '' ? null : dayjs(value).toISOString())

const NoticeConfigPage = () => {
  const [config, setConfig] = useState<NoticeConfig>(DEFAULT_NOTICE_CONFIG)
  const [status, setStatus] = useState(getNoticeConfigStatus())
  const [saving, setSaving] = useState(false)
  const [preview, setPreview] = useState(false)
  const [authOpen, setAuthOpen] = useState(false)
  const [password, setPassword] = useState('')
  /* 저장은 nginx Basic 인증을 통과해야 한다.
     비밀번호를 코드나 localStorage 에 두면 인증 의미가 없으므로,
     저장할 때 입력받아 같은 화면에 머무는 동안만 메모리에 들고 있는다. */
  const credentialRef = useRef<string | null>(null)

  const refresh = async () => {
    const loaded = await loadNoticeConfig()
    setConfig({...loaded, body: [...loaded.body], highlight: loaded.highlight && {...loaded.highlight}})
    setStatus(getNoticeConfigStatus())
  }

  useEffect(() => {
    refresh()
  }, [])

  const update = (patch: Partial<NoticeConfig>) => setConfig((prev) => ({...prev, ...patch}))

  const updateHighlight = (patch: Partial<NoticeHighlight>) =>
    setConfig((prev) => ({
      ...prev,
      highlight: {...(prev.highlight ?? EMPTY_HIGHLIGHT), ...patch},
    }))

  /** 저장 전 검증. 화면에서 만든 값이라도 기간 역전 같은 실수는 걸러야 한다. */
  const validate = (): string | null => {
    const parsed = parseNoticeConfig(JSON.parse(stringifyNoticeConfig(config)))
    return typeof parsed === 'string' ? parsed : null
  }

  const download = () => {
    const error = validate()
    if (error) {
      normalAlert(error)
      return
    }
    const blob = new Blob([stringifyNoticeConfig(config)], {type: 'application/json'})
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'notice-config.json'
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  /** 저장 버튼. 비밀번호가 아직 없으면 먼저 입력받는다. */
  const requestSave = () => {
    const error = validate()
    if (error) {
      normalAlert(error)
      return
    }
    if (!credentialRef.current) {
      setAuthOpen(true)
      return
    }
    save()
  }

  const confirmPassword = () => {
    try {
      credentialRef.current = btoa(`${CONFIG_AUTH_USER}:${password}`)
    } catch {
      /* btoa 는 한글 등 비 ASCII 를 처리하지 못한다 */
      normalAlert('비밀번호는 영문·숫자·기호만 사용할 수 있습니다.')
      return
    }
    setPassword('')
    setAuthOpen(false)
    save()
  }

  const save = async () => {
    setSaving(true)
    try {
      const res = await fetch(NOTICE_CONFIG_PATH, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${credentialRef.current}`,
        },
        body: stringifyNoticeConfig(config),
      })

      if (res.status === 401 || res.status === 403) {
        /* 잘못된 비밀번호를 계속 들고 있으면 다음 저장도 막히므로 버린다 */
        credentialRef.current = null
        await normalAlert('비밀번호가 올바르지 않습니다. 다시 시도해주세요.')
        return
      }
      if (!res.ok) {
        await normalAlert(
          `저장하지 못했습니다. (HTTP ${res.status})\n서버에 쓰기 설정이 되어 있는지 확인해주세요.`
        )
        return
      }

      /* PUT 응답만 믿지 않고 다시 읽어 실제 반영을 확인한다 */
      await refresh()
      await resultAlert({resultCode: 0})
    } catch {
      await normalAlert('저장 요청에 실패했습니다. 네트워크 상태를 확인해주세요.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={'card card-body'}>
      {/* 서버에 직접 접속할 수 없는 환경이라, 지금 무엇이 적용 중인지 화면이 말해줘야 한다 */}
      {status.source === 'remote' ? (
        <Alert severity={config.enabled ? 'success' : 'info'} className={'mb-4 fs-5'}>
          설정 파일이 적용 중입니다. 공지는 {config.enabled ? '켜져' : '꺼져'} 있습니다.
          {status.loadedAt && ` (확인 시각 ${status.loadedAt.toLocaleString('ko-KR')})`}
        </Alert>
      ) : (
        <Alert severity={'warning'} className={'mb-4 fs-5'}>
          공지 설정 파일이 없어 팝업이 뜨지 않습니다. {status.error ?? ''}
        </Alert>
      )}

      <div className={'mb-5'}>
        <FormControlLabel
          control={
            <Switch
              checked={config.enabled}
              onChange={(e) => update({enabled: e.target.checked})}
            />
          }
          label={<span className={'fs-4 fw-bold'}>공지 사용</span>}
        />
        <p className={'text-muted fs-6 mb-0'}>
          끄면 문구는 남겨둔 채 팝업만 사라집니다. 노출 기간이 지나도 자동으로 사라집니다.
        </p>
      </div>

      <div className={'mb-5'}>
        <h3 className={'fw-bold fs-4 mb-1'}>공지 번호</h3>
        <p className={'text-muted fs-6'}>
          "보지 않기"를 누른 사용자에게도 다시 뜨게 하려면 새 공지마다 값을 바꿔야 합니다.
        </p>
        <div className={'d-flex flex-wrap gap-3 align-items-center'}>
          <TextField
            size={'small'}
            value={config.id}
            placeholder={'2026-08-maintenance'}
            onChange={(e) => update({id: e.target.value})}
            sx={{minWidth: 280}}
          />
          <Button
            variant={'outlined'}
            color={'inherit'}
            onClick={() => update({id: `notice-${dayjs().format('YYYYMMDD-HHmm')}`})}
          >
            새 번호 만들기
          </Button>
        </div>
      </div>

      <div className={'mb-5'}>
        <h3 className={'fw-bold fs-4 mb-3'}>노출 기간</h3>
        <div className={'d-flex flex-wrap gap-3'}>
          <TextField
            type={'datetime-local'}
            size={'small'}
            label={'시작'}
            InputLabelProps={{shrink: true}}
            value={toInputValue(config.startAt)}
            onChange={(e) => update({startAt: fromInputValue(e.target.value)})}
          />
          <TextField
            type={'datetime-local'}
            size={'small'}
            label={'종료'}
            InputLabelProps={{shrink: true}}
            value={toInputValue(config.endAt)}
            onChange={(e) => update({endAt: fromInputValue(e.target.value)})}
          />
        </div>
        <p className={'text-muted fs-6 mt-2 mb-0'}>비워두면 기간 제한 없이 노출합니다.</p>
      </div>

      <div className={'mb-5'}>
        <h3 className={'fw-bold fs-4 mb-3'}>제목</h3>
        <TextField
          fullWidth
          size={'small'}
          value={config.title}
          placeholder={'서버 점검 공지'}
          onChange={(e) => update({title: e.target.value})}
        />
      </div>

      <div className={'mb-5'}>
        <h3 className={'fw-bold fs-4 mb-1'}>본문</h3>
        <p className={'text-muted fs-6'}>줄바꿈한 대로 표시됩니다. 빈 줄은 한 줄 띄웁니다.</p>
        <TextField
          fullWidth
          multiline
          minRows={4}
          value={config.body.join('\n')}
          onChange={(e) => update({body: e.target.value.split('\n')})}
        />
      </div>

      <div className={'mb-5'}>
        <FormControlLabel
          control={
            <Switch
              checked={config.highlight !== null}
              onChange={(e) => update({highlight: e.target.checked ? EMPTY_HIGHLIGHT : null})}
            />
          }
          label={<span className={'fs-4 fw-bold'}>강조 박스 사용</span>}
        />
        {config.highlight && (
          <div className={'d-flex flex-wrap gap-3 mt-3'}>
            <TextField
              size={'small'}
              label={'배지'}
              value={config.highlight.label}
              placeholder={'점검 일시 안내'}
              onChange={(e) => updateHighlight({label: e.target.value})}
            />
            <TextField
              size={'small'}
              label={'첫 줄'}
              value={config.highlight.date}
              placeholder={'2026년 8월 14일(금요일)'}
              onChange={(e) => updateHighlight({date: e.target.value})}
            />
            <TextField
              size={'small'}
              label={'둘째 줄'}
              value={config.highlight.time}
              placeholder={'12:00 ~ 13:00'}
              onChange={(e) => updateHighlight({time: e.target.value})}
            />
            <TextField
              size={'small'}
              label={'보조 설명'}
              value={config.highlight.note}
              placeholder={'상황에 따라 작업 시간이 다소 변동 될 수 있습니다.'}
              onChange={(e) => updateHighlight({note: e.target.value})}
              sx={{minWidth: 320}}
            />
          </div>
        )}
      </div>

      <div className={'mb-5'}>
        <FormControlLabel
          control={
            <Switch
              checked={config.allowDismiss}
              onChange={(e) => update({allowDismiss: e.target.checked})}
            />
          }
          label={<span className={'fs-4 fw-bold'}>"다음 공지까지 보지 않기" 사용</span>}
        />
        <p className={'text-muted fs-6 mb-0'}>
          체크한 사용자에게는 공지 번호가 바뀔 때까지 뜨지 않습니다. 끄면 확인만 할 수 있어
          접속할 때마다 다시 뜹니다 — 반드시 봐야 하는 공지에 사용하세요.
        </p>
      </div>

      <div className={'d-flex flex-wrap gap-3'}>
        <Button variant={'contained'} onClick={requestSave} disabled={saving}>
          {saving ? '저장 중...' : '저장'}
        </Button>
        <Button variant={'outlined'} onClick={() => setPreview(true)}>
          미리보기
        </Button>
        <Button variant={'outlined'} onClick={download}>
          JSON 다운로드
        </Button>
        <Button variant={'outlined'} color={'inherit'} onClick={refresh}>
          다시 불러오기
        </Button>
        {/* 내 화면에서 공지를 이미 숨겼다면 결과를 확인할 수 없으니 되돌릴 수단을 둔다 */}
        <Button
          variant={'text'}
          color={'inherit'}
          onClick={async () => {
            clearNoticeDismissal()
            await normalAlert('이 기기의 숨김 기록을 지웠습니다. 새로고침하면 다시 표시됩니다.')
          }}
        >
          이 기기 숨김 해제
        </Button>
      </div>

      {preview && <NoticeModal preview={config} onPreviewClose={() => setPreview(false)} />}

      <Dialog open={authOpen} onClose={() => setAuthOpen(false)} fullWidth maxWidth={'xs'}>
        <DialogTitle className={'fw-bold'}>저장 비밀번호</DialogTitle>
        <DialogContent>
          <p className={'text-muted fs-6 mb-3'}>
            설정을 저장하려면 비밀번호가 필요합니다. 이 화면을 벗어나면 다시 입력해야 합니다.
          </p>
          <TextField
            autoFocus
            fullWidth
            type={'password'}
            label={'비밀번호'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirmPassword()
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button color={'inherit'} onClick={() => setAuthOpen(false)}>
            취소
          </Button>
          <Button variant={'contained'} onClick={confirmPassword} disabled={password === ''}>
            저장
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default NoticeConfigPage
